import { z } from 'zod';

import type { Customization } from '@nucleus-commerce/api-client/admin';
import { isMoney, type Money } from '@nucleus-commerce/core';

import { validateCustomizations, type CustomizationValues } from './customization-rules';

const moneyValue = z.custom<Money>(isMoney).nullable();

const QUANTITY = 'draftOrders:lines.validation.quantity';

/**
 * Typed as text so a half-typed number stays in the input; 1 to 99 like the backend. Stays a
 * string in the output too (FormField wants output assignable to input); mappers convert.
 */
export const quantitySchema = z
  .string()
  .trim()
  .refine((v) => /^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 99, { error: QUANTITY });

const catalogLineFields = {
  quantity: quantitySchema,
  overridePrice: z.boolean(),
  unitPrice: moneyValue,
  customizations: z.record(z.string(), z.union([z.string(), z.boolean()])),
};

function refineCatalogLine(defs: readonly Customization[]) {
  return (
    v: { overridePrice: boolean; unitPrice: Money | null; customizations: CustomizationValues },
    ctx: z.RefinementCtx,
  ) => {
    if (v.overridePrice) {
      if (v.unitPrice === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['unitPrice'],
          message: 'draftOrders:picker.validation.priceRequired',
        });
      } else if (v.unitPrice.amount < 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['unitPrice'],
          message: 'draftOrders:picker.validation.priceNegative',
        });
      }
    }
    const errors = validateCustomizations(defs, v.customizations);
    for (const [key, message] of Object.entries(errors)) {
      ctx.addIssue({ code: 'custom', path: ['customizations', key], message });
    }
  };
}

/**
 * A catalog line. The customization checks need the product's definitions, hence the factory;
 * the override price is only read when the switch is on.
 */
export function productLineSchema(defs: readonly Customization[]) {
  return z
    .object({
      storeProductId: z.string().min(1, { error: 'draftOrders:picker.validation.product' }),
      variantId: z.string().min(1, { error: 'draftOrders:picker.validation.variant' }),
      ...catalogLineFields,
    })
    .superRefine(refineCatalogLine(defs));
}

export type ProductLineFormInput = z.input<ReturnType<typeof productLineSchema>>;
export type ProductLineFormOutput = z.output<ReturnType<typeof productLineSchema>>;

/** A catalog line already on the draft: its variant stays, the rest can change in place. */
export function lineEditSchema(defs: readonly Customization[]) {
  return z.object(catalogLineFields).superRefine(refineCatalogLine(defs));
}

export type LineEditFormInput = z.input<ReturnType<typeof lineEditSchema>>;
export type LineEditFormOutput = z.output<ReturnType<typeof lineEditSchema>>;

/** A one-off item typed by staff: a sketch fee, a box upgrade, a resizing service. */
export const customLineSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, { error: 'draftOrders:customLine.validation.title' })
    .max(200, { error: 'draftOrders:customLine.validation.titleLength' }),
  sku: z.string().trim().max(60, { error: 'draftOrders:customLine.validation.sku' }),
  quantity: quantitySchema,
  unitPrice: moneyValue
    .pipe(z.custom<Money>(isMoney, { error: 'draftOrders:customLine.validation.price' }))
    .refine((m) => m.amount >= 0, {
      error: 'draftOrders:customLine.validation.price',
    }),
  requiresShipping: z.boolean(),
});

export type CustomLineFormInput = z.input<typeof customLineSchema>;
export type CustomLineFormOutput = z.output<typeof customLineSchema>;
